import { Outlet } from 'react-router'
import { useState, useEffect } from 'react'
import {
  ChevronLeft,
  ChevronRight,
  Command,
  LogOut,
  Moon,
  ShieldCheck,
  Sun,
  User,
} from 'lucide-react'
import { AdminSidebarNav } from './admin-sidebar-nav'
import { CommandPalette } from '@/components/shared/command-palette'
import { FooterBar } from '@/components/shared/footer-bar'
import { useAuth } from '@/providers/auth-provider'
import { useTheme } from '@/providers/theme-provider'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'

const COLLAPSE_BREAKPOINT = 1024

/**
 * AdminLayout — full dashboard shell for the admin panel.
 *
 * Structure:
 *   ┌─────────────────────────────────────────────┐
 *   │ [Sidebar]  │  [Header]                       │
 *   │            │  [Content — Outlet]              │
 *   └─────────────────────────────────────────────┘
 *
 * Sidebar collapses to icons at <1024px. No bottom tabs (admin is desktop-primary).
 */
export function AdminLayout() {
  const { user, logout } = useAuth()
  const { resolvedTheme, setTheme } = useTheme()
  const [commandOpen, setCommandOpen] = useState(false)

  // Auto-collapse at narrow viewports
  const [collapsed, setCollapsed] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < COLLAPSE_BREAKPOINT
  )

  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${COLLAPSE_BREAKPOINT - 1}px)`)
    const handler = (e: MediaQueryListEvent) => setCollapsed(e.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])

  // ⌘K / Ctrl+K — open command palette
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setCommandOpen(true)
      }
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [])

  return (
    <div className="flex h-screen overflow-hidden bg-[radial-gradient(circle_at_top_left,_rgba(3,102,102,0.12),_transparent_28%),radial-gradient(circle_at_top_right,_rgba(15,23,42,0.1),_transparent_26%),linear-gradient(180deg,_rgba(2,6,23,0.04),_transparent_18%),linear-gradient(180deg,_var(--color-background),_var(--color-background))]">
      {/* ── Sidebar ──────────────────────────────────────────────────── */}
      <aside
        className={cn(
          'flex shrink-0 flex-col border-r border-white/10 bg-[linear-gradient(180deg,rgba(8,15,32,0.98),rgba(10,20,38,0.96))] text-white transition-all duration-200',
          collapsed ? 'w-14' : 'w-56'
        )}
      >
        {/* Sidebar header */}
        <div className={cn('flex h-20 items-center border-b border-white/10 px-3', collapsed && 'justify-center')}>
          {!collapsed && (
            <div className="w-full rounded-3xl border border-white/10 bg-white/5 px-4 py-3 shadow-[0_20px_45px_rgba(0,0,0,0.22)] backdrop-blur">
              <div className="flex items-center justify-between gap-3">
                <div className="space-y-2">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-white/55">Operator console</p>
                </div>
              </div>
            </div>
          )}
          {collapsed && (
            <div className="h-8 w-8" />
          )}
        </div>

        {/* Navigation */}
        <div className="flex-1 overflow-y-auto">
          <AdminSidebarNav collapsed={collapsed} />
        </div>

        {!collapsed && (
          <div className="px-3 pb-3">
            <div className="rounded-3xl border border-white/10 bg-white/5 p-4 text-white shadow-[0_18px_40px_rgba(0,0,0,0.16)] backdrop-blur">
              <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.24em] text-white/55">
                <ShieldCheck className="h-3.5 w-3.5" />
                Foundation
              </div>
              <p className="mt-3 text-sm font-medium">Dashboard rebuild slice</p>
              <p className="mt-1 text-xs leading-5 text-white/68">
                Auth, shell, and dashboard foundations are live. Modules return one rebuilt seam at a time.
              </p>
              <div className="mt-4 rounded-2xl border border-white/10 bg-black/10 px-3 py-2 text-[11px] text-white/70">
                Internal operators stay in one control plane while tenant-facing modules come back as isolated slices.
              </div>
            </div>
          </div>
        )}

        {/* Collapse toggle */}
        <div className="border-t border-white/10 p-2">
          <button
            onClick={() => setCollapsed(c => !c)}
            className="flex w-full items-center justify-center rounded-2xl p-2 text-white/80 transition-colors hover:bg-white/8 hover:text-white"
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </button>
        </div>
      </aside>

      {/* ── Main area ────────────────────────────────────────────────── */}
      <div className="flex flex-col flex-1 min-w-0">
        {/* Header */}
        <header className="shrink-0 border-b border-border/70 bg-background/82 px-4 py-4 backdrop-blur md:px-6">
          <div className="flex h-full items-center justify-between gap-4">
            <div className="space-y-2">
              <img src="/branding/360dashwithboxsmall.svg" alt="360Dash" className="h-6 w-auto" />
              <div className="inline-flex items-center gap-2 rounded-full border border-border/70 bg-background/80 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.24em] text-muted-foreground shadow-sm">
                <ShieldCheck className="h-3.5 w-3.5" />
                Admin session
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                className="hidden gap-2 rounded-full border border-border/70 bg-background/80 px-3 text-muted-foreground shadow-sm sm:flex"
                onClick={() => setCommandOpen(true)}
              >
                <Command className="h-3.5 w-3.5" />
                <span className="text-xs">Command</span>
                <kbd className="pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] text-muted-foreground">
                  <span className="text-xs">⌘</span>K
                </kbd>
              </Button>

              <Button
                variant="ghost"
                size="icon"
                className="rounded-full"
                onClick={() => setTheme(resolvedTheme === 'dark' ? 'light' : 'dark')}
                aria-label="Toggle theme"
              >
                {resolvedTheme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="rounded-full" aria-label="User menu">
                    <User className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <div className="px-2 py-1.5 text-sm">
                    <p className="font-medium">{user?.email}</p>
                    <p className="text-xs text-muted-foreground capitalize">{user?.type}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} className="text-destructive">
                    <LogOut className="mr-2 h-4 w-4" />
                    Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto">
          <div className="mx-auto max-w-7xl p-4 md:p-6 lg:p-8">
            <Outlet />
          </div>
        </main>
        <FooterBar />
      </div>

      {/* Command palette — portal renders outside layout DOM tree */}
      <CommandPalette
        open={commandOpen}
        onOpenChange={setCommandOpen}
        role="admin"
      />
    </div>
  )
}
